/**
 * SSE Client
 * 
 * Uses the Fetch API with an Authorization: Bearer header to connect to the SSE endpoint.
 * This is consistent with how all other API calls in this app authenticate (via axiosPrivate
 * which adds the Bearer token to the Authorization header).
 * 
 * Unlike native EventSource, fetch() supports custom headers and can be used for SSE
 * by reading the response body as a stream.
 */

import BASE_URL from '../../api/base.js';

let abortController = null;
let isConnecting = false;

/**
 * Connect to the SSE endpoint using fetch with Authorization header
 * @param {string} token - JWT access token
 * @param {function} onMessage - Callback for incoming messages: (event: object) => void
 * @param {function} [onError] - Callback for connection errors: (error: Error) => void
 */
const connect = async (token, onMessage, onError) => {
    if (isConnecting) {
        console.log('[SSE Client] Already connecting, skipping');
        return;
    }

    if (abortController) {
        console.log('[SSE Client] Already connected, disconnecting first');
        disconnect();
    }

    isConnecting = true;
    abortController = new AbortController();

    const url = `${BASE_URL}/api/events`;
    console.log('[SSE Client] Connecting to SSE endpoint via fetch');

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'text/event-stream',
                'Cache-Control': 'no-cache',
            },
            signal: abortController.signal,
        });

        isConnecting = false;

        if (!response.ok) {
            const error = new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
            console.error('[SSE Client]', error.message);
            if (onError) onError(error);
            return;
        }

        console.log('[SSE Client] Connection opened');

        // Read the response body as a stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const readStream = async () => {
            try {
                while (true) {
                    const { done, value } = await reader.read();

                    if (done) {
                        console.log('[SSE Client] Stream ended');
                        break;
                    }

                    buffer += decoder.decode(value, { stream: true });

                    // SSE messages are separated by double newlines
                    const lines = buffer.split('\n\n');
                    buffer = lines.pop(); // Keep incomplete last chunk in buffer

                    for (const chunk of lines) {
                        if (!chunk.trim()) continue;

                        // Parse SSE data lines
                        for (const line of chunk.split('\n')) {
                            if (line.startsWith('data: ')) {
                                const data = line.slice(6).trim();
                                if (data) {
                                    try {
                                        const event = JSON.parse(data);
                                        onMessage(event);
                                    } catch (err) {
                                        console.error('[SSE Client] Failed to parse event data:', err);
                                    }
                                }
                            }
                            // Ignore comment lines (heartbeat: ": ping")
                        }
                    }
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('[SSE Client] Connection aborted');
                } else {
                    console.error('[SSE Client] Stream read error:', err);
                    if (onError) onError(err);
                }
            }
        };

        readStream();

    } catch (err) {
        isConnecting = false;
        if (err.name === 'AbortError') {
            console.log('[SSE Client] Connection aborted before opening');
        } else {
            console.error('[SSE Client] Connection error:', err);
            if (onError) onError(err);
        }
    }
};

/**
 * Disconnect from the SSE endpoint
 */
const disconnect = () => {
    if (abortController) {
        abortController.abort();
        abortController = null;
        isConnecting = false;
        console.log('[SSE Client] Connection closed');
    }
};

/**
 * Check if currently connected
 * @returns {boolean}
 */
const isConnected = () => {
    return abortController !== null && !abortController.signal.aborted;
};

export { connect, disconnect, isConnected };
